import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import type { ApiError } from "@/api/types";

/**
 * Maps API field-level validation errors to React Hook Form inline errors.
 * Returns errors whose `field` doesn't match a registered form field (or
 * has no `field` at all) — callers should display these as a root/banner error.
 *
 * Uses a try/catch around setError: RHF throws for unregistered field paths
 * in strict mode, and in non-strict mode the error simply won't render.
 * Either way, we surface it as unhandled so the user always sees feedback.
 */
export function applyFieldErrors<T extends FieldValues>(
  errors: ApiError[],
  setError: UseFormSetError<T>,
  registeredFields?: Set<string>,
): ApiError[] {
  const unhandled: ApiError[] = [];
  for (const err of errors) {
    const message = friendlyErrorMessage(err.message);
    if (err.field && (!registeredFields || registeredFields.has(err.field))) {
      setError(err.field as Path<T>, { message });
    } else {
      unhandled.push({ ...err, message });
    }
  }
  return unhandled;
}

function friendlyErrorMessage(message: string): string {
  const normalized = message.trim();
  const lower = normalized.toLowerCase();

  if (lower === "can't be blank" || lower === "is required") {
    return "This field is required.";
  }
  if (lower === "price is not valid" || lower === "is not a valid price") {
    return "Enter a valid price.";
  }
  if (lower === "enter a valid email") {
    return "Enter a valid email.";
  }
  if (!normalized) {
    return "Something went wrong. Please try again.";
  }

  return normalized[0]?.toUpperCase() === normalized[0]
    ? normalized
    : `${normalized[0]?.toUpperCase()}${normalized.slice(1)}`;
}
